import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useRange } from '../../range';
import useSort, { applySort } from '../../hooks/useSort';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import { formatDateTime, formatRelative } from '../../format';

/**
 * System › Audit — every admin action, reads included.
 *
 * The middleware writes a row for every authenticated request to this API,
 * not only the state-changing ones, which is what makes "viewing someone's
 * transcript is as auditable as deleting their account" true rather than
 * aspirational.
 *
 * Now driven by the shell's global range like everything else, so switching
 * to Audit after noticing something odd on another page shows the same
 * window rather than silently resetting to the last 24 hours.
 */
export default function AuditPage() {
  const range = useRange();
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [applied, setApplied] = useState({ actorId: '', action: '' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { sort, onSort } = useSort();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { since: range.since, until: range.until };
      if (applied.actorId.trim()) params.actor_id = applied.actorId.trim();
      if (applied.action.trim()) params.action = applied.action.trim();
      const { data } = await adminApi.get('/audit', { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load audit log'));
    } finally {
      setLoading(false);
    }
  }, [range.since, range.until, applied]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    {
      key: 'ts',
      label: 'When',
      sortable: true,
      render: (r) => <span title={formatDateTime(r.ts)}>{formatRelative(r.ts)}</span>,
    },
    { key: 'admin_id', label: 'Actor', sortable: true, render: (r) => <span className="font-mono text-[11px]">{r.admin_id}</span> },
    { key: 'action', label: 'Action', sortable: true, render: (r) => <span className="font-medium text-ink">{r.action || `${r.method} ${r.path}`}</span> },
    {
      key: 'target',
      label: 'Target',
      render: (r) => (r.target_type ? <span className="font-mono text-[11px]">{r.target_type}:{r.target_id}</span> : null),
    },
    { key: 'reason', label: 'Reason', render: (r) => r.reason || null },
    {
      key: 'status_code',
      label: 'Status',
      align: 'right',
      sortable: true,
      render: (r) => (
        <span className={r.status_code < 400 ? 'text-status-good' : 'text-status-critical'}>{r.status_code}</span>
      ),
    },
  ], []);

  const sortedItems = useMemo(() => applySort(items, sort, columns), [items, sort, columns]);

  return (
    <div>
      <PageHeader
        title="Audit"
        hint={`Every admin action — reads included, not just writes. ${range.label}.`}
      />

      <form
        onSubmit={(e) => { e.preventDefault(); setApplied({ actorId, action }); }}
        className="mb-4 flex flex-wrap gap-2"
      >
        <input
          placeholder="Actor id (adm_…)"
          value={actorId}
          onChange={(e) => setActorId(e.target.value)}
          className="min-w-[200px] flex-1 rounded-lg border border-hairline px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
        <input
          placeholder="Action (e.g. ops.flag.updated)"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="min-w-[220px] flex-1 rounded-lg border border-hairline px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
        <button
          type="submit"
          className="rounded-lg border border-hairline px-3 py-1.5 text-sm font-medium text-ink-secondary hover:bg-surface-page hover:text-ink"
        >
          Filter
        </button>
        {(applied.actorId || applied.action) && (
          <button
            type="button"
            onClick={() => { setActorId(''); setAction(''); setApplied({ actorId: '', action: '' }); }}
            className="text-xs font-medium text-ink-secondary hover:text-ink"
          >
            Clear ✕
          </button>
        )}
      </form>

      <DataTable
        columns={columns}
        rows={sortedItems}
        rowKey={(r) => r.log_id}
        loading={loading}
        dense
        maxHeight="36rem"
        sort={sort}
        onSort={onSort}
        empty={
          <EmptyState
            title="No matching entries"
            detail="Widen the time range in the top bar, or clear the actor and action filters."
          />
        }
      />
    </div>
  );
}
